import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { EyeIcon, Flex, IconButton } from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'
import type { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import { PostMiniatureButton } from '~publish/pages/Community/PostMiniature'

import { CommentMetrics } from '../CommentMetrics'
import { PostNavigationArrows } from './PostNavigationArrows'
import type { PostListItem } from '../types'

import styles from './CompactPostNavigationCard.module.css'

import type { JSX } from 'react'

const OPEN_DELAY = 300
const CLOSE_DELAY = 200

type CompactPostNavigationCardProps = {
  post: FragmentOf<typeof CommentsPostCard_Post>
  postId: string
  posts?: PostListItem[]
  selectedPostId?: string
  onPostSelect?: (postId: string) => void
  isScrolledPast?: boolean
  panelToggle?: React.ReactNode
}

export function CompactPostNavigationCard({
  post,
  postId,
  posts,
  selectedPostId,
  onPostSelect,
  isScrolledPast,
  panelToggle,
}: CompactPostNavigationCardProps): JSX.Element {
  const showNavigation =
    posts && posts.length > 1 && selectedPostId && onPostSelect

  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false)
  const openTimeout = React.useRef<ReturnType<typeof setTimeout>>()
  const closeTimeout = React.useRef<ReturnType<typeof setTimeout>>()

  const openPreview = React.useCallback((): void => {
    clearTimeout(closeTimeout.current)
    openTimeout.current = setTimeout(() => setIsPreviewOpen(true), OPEN_DELAY)
  }, [])

  const closePreview = React.useCallback((): void => {
    clearTimeout(openTimeout.current)
    closeTimeout.current = setTimeout(
      () => setIsPreviewOpen(false),
      CLOSE_DELAY,
    )
  }, [])

  React.useEffect(() => {
    if (!isScrolledPast) {
      setIsPreviewOpen(false)
    }
  }, [isScrolledPast])

  React.useEffect(() => {
    return (): void => {
      clearTimeout(openTimeout.current)
      clearTimeout(closeTimeout.current)
    }
  }, [])

  return (
    <div className={styles.compactCardGrid}>
      <Flex
        align="center"
        gap="sm"
        className={styles.compactCard}
        data-scrolled={isScrolledPast || undefined}
      >
        {panelToggle}
        {isScrolledPast && (
          <div
            className={styles.peekContainer}
            onMouseEnter={openPreview}
            onMouseLeave={closePreview}
          >
            <IconButton
              label="Preview post"
              size="small"
              variant="tertiary"
              onFocus={openPreview}
              onBlur={closePreview}
            >
              <EyeIcon />
            </IconButton>
            <AnimatePresence>
              {isPreviewOpen && (
                <m.div
                  className={styles.previewOverlay}
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.3 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <PostMiniatureButton post={post} />
                </m.div>
              )}
            </AnimatePresence>
          </div>
        )}
        <CommentMetrics
          postId={postId}
          variant="compact"
          className={styles.metrics}
        />
        {showNavigation && (
          <PostNavigationArrows
            posts={posts}
            selectedPostId={selectedPostId}
            onPostSelect={onPostSelect}
            direction="horizontal"
          />
        )}
      </Flex>
    </div>
  )
}
